// @flow

import _ from 'lodash';

import type { rendererArguments } from 'app/rgs/utils';
import { cache } from 'app/v2/common/decorators/method';

// app-my-navigation
import type { IMenuConfiguration, MenuItemModel } from 'app-my-navigation/types.flow';
import { usingCfgStrategy } from 'app-my-navigation/configuration';
import menuController from './js/controller';
import type { MenuController } from './js/controller';

type ElementView = { value: TElementView | (MenuItem: *) => string | HTMLElement }

export type TElementView = {
	key: string,
	...ElementView
}

export type TUrlValue = {
	key: string,
	value: string | number | boolean,
}

export type TUrlValues = TUrlValue[] | TUrlValue;

type presetArguments = {
	cfg: *,
	model: *,
	view: rendererArguments,
}

/*
	e.g.
	import menuApi from 'nfd-my-navigation/api';
	1) to replace html of item on their template (by default: just Title), you need:

		Example:
		'Dms.DmsNotifications' is a key belong every item from response
		Outer: Dms 'Корпоративный ДМС'-> Inner: DmsNotification 'Уведомления'

		value (3):
			1) callback -> will get item data from response as first arg.
			2) pureComputed -> will return html/string;
			3) html/string

		menuApi.addElementViewProvider('Dms.DmsNotifications', {
			value: this._dmsPageModel.notifications.htmlView,
		});

	2) to replace URL on their scheme, if server passes 'private/dms-insurance#{policyid:}/somethingelse;
		You may replace {policyId} on some their value (instead policyId may be rgsId and so on);

		For policyId: url = {key: 'policyId', value: '3411'}

		menuApi.addUrlChunks(url); where URL is type TUrlValue (see above  TUrlValue flowjs)
		menuApi.addUrlListener(urls); where URLs is array of type TUrlValue (see above TUrlValue flowjs)

		Using: app-my-dms-insurance/route-config; app-my-dms-insurance/page/page-controller;
 */

export class MenuApi {
	_menuController: MenuController;
	_runner: Function[];

	constructor (controller: MenuController) {
		this._runner = [];
		this._menuController = controller;
	}

	//WARNING! It's deprecated, you don't have to use it.
	//use cached getMenuItemsAsync instead
	onController (...args: [string, Function]) {
		this._menuController.on(...args);
	}

	async getItemAsync (key: string): MenuItemModel {
		const items = await this.getMenuItemsAsync();

		const concreteItem = this.getItemWithKey((items || {}).Items, key);

		return concreteItem;
	}

	@cache('30s')
	async getMenuItemsAsync (): Promise<any> { //result of method cached, you may call this over 1000 times
		return await this._menuController.fetchMenuItems();
	}

	getItemWithKey (items: any, key: string): any { //static may be) mini-helper
		for (const item of items) {
			if (item.Key === key) {
				return item;
			}

			const child = this.getItemWithKey(item.Items, key);
			if (child) {
				return child;
			}
		}
	}

	addElementViewProvider (key: string, params: ElementView): void {
		this._menuController.sayAboutRerenderingWithKey({
			key, //format by tree Dms.DmsNotification, example, may be First.Second and so on;
			...params,
		});
	}

	addUrlListener (data: TUrlValue): void {
		this._menuController.updateUrlDependencies(data);
	}

	addUrlChunks (items: TUrlValues): void {
		this._menuController.updateUrlDependencies(items);
	}

	async getActiveMenuKey (): Promise<string> {

		return await this._menuController.getCurrentMenuKey();
	}

	_runAfterModelInit (callback: Function): void {
		this._runner.push(callback);
	}

	injectConfiguration (cfg: IMenuConfiguration): void {
		this._callIfModelCreated(() => this._menuController.withCfg(cfg));
	}

	_initControllerCore (preset: presetArguments): void {
		this._menuController.init();
		this._menuController
			.withView(preset.view);
	}

	_getRender () {
		return {render: this._menuController.render};
	}

	_startPlannedRunnersAfter (): void {
		this._runner.forEach((action: Function) => action());

		this._runner = [];
	}

	setup (preset: presetArguments) {
		this._initControllerCore(preset);
		this._startPlannedRunnersAfter();

		return this._getRender();
	}

	setActiveMenuItemByKey (key: string): void {
		this._menuController.setActiveItemByKey(key);
	}

	_callIfModelCreated (callback: Function) {
		return this._menuController.hasModel ? callback() : this._runAfterModelInit(callback);
	}

	update = async (): Promise<void> => {
		const data = await this._menuController.fetchMenuItems();

		this._callIfModelCreated(() => this._menuController.setState(data));
	};

	getDefaultCfg (): IMenuConfiguration {
		return usingCfgStrategy.createDefaultMenu();
	}

	// later will be refactored
	// workaround
	async getUrlItemByKey (key: string): Promise<string> {
		const menuItems = await this._menuController.fetchSilentMenuItems();

		const parse = (item: MenuItemModel): string => this.getDefaultCfg().parseUrl()(item.Url);
		const isSuitableKey = (item: MenuItemModel) => item.Key === key;

		const goDeep = (item: MenuItemModel): string => {
			const items = item.Items || [];

			let index = -1;

			while (++index < items.length) {
				const current = items[index];

				if (isSuitableKey(current)) {
					return parse(current);
				}

				const nestedUrl = goDeep(current);

				if (nestedUrl) {
					return nestedUrl;
				}
			}

			return _.stubString();
		};

		return goDeep(menuItems);
	}

	hasAccess = async (menuItemKey: string) => {
		return await this.getItemAsync(menuItemKey)
			.then((menuItem) => {
				return !_.isNil(menuItem);
			});
	};
}

export default new MenuApi(menuController);
